import React, {useEffect, useState} from 'react'
import {Box, withStyles} from '@material-ui/core';
import PropTypes from 'prop-types';
import GridBlock from 'components/GridBlock';
import TextInput from 'components/TextInput';
import Tr from 'translations/Translations';
import CPVInput from 'components/CPVInput';
import NetGrossField from 'components/NetGrossField';
import PositionsSection from './PositionsSection'
import ConditionsSection from './ConditionsSection'
import CriteriaSection from './CriteriaSection'
import getObjectValue from 'libs/getObjectValue';
import Loading from 'components/Loading';
import {withRouter} from "react-router";
import DealingsService from 'services/DealingsService';
import AdditionalPositionsSection from './AdditionalPositionsSection';
import TitledComponent from 'components/TitledComponent';
import INPUT_TYPE from 'constants/INPUT_TYPE';
import InputLimits from 'constants/InputLimits';
import mergeDealing from '../lib/mergeDealing';
import RadioGroup from "components/RadioGroup";
import DatePicker from "components/DatePicker";
import Select, {createSelectItem} from "components/Select";
import {isNonpublicProcedure} from "libs/getDemandStatus";
import Confirm from "components/popup/confirm/Confirm";
import PositionsParametersSection from "./PositionsParametersSection";


const style = theme => ({
    root: {
        '&>*': {
            width: 'inherit'
        },
        paddingLeft: theme.spacing(4)
    },
    grossNetGrid: {
        '&>*': {
            flexGrow: 1,
            flexBasis: 0,

            '&:not(:first-child)': {
                marginLeft: theme.spacing(2),
            }
        }
    },
    cpvBox: {
        marginBottom: theme.spacing(2),
    },
    loading: {
        height: 100
    }
})

const limits = InputLimits.procedure.dealings;

function PartForm(
    {
        classes,
        formikProps,
        dirtyListenerSemaphore,
        id,
        match,
        isDealingElement,
        dealingsList,
        autoSaveFormDraftActive
    }) {
    const createId = (key) => `${id}.${key}`;
    const partId = getObjectValue(createId('identity'), formikProps.values)
    const demandId = match.params.id;
    const [loaded, setLoaded] = useState(isDealingElement);
    const [draftConfirm, setDraftConfirm] = useState(false);
    const [working, setWorking] = useState(false);

    useEffect(() => {
        if (demandId && !isDealingElement) {
            DealingsService.get(demandId, partId)
                .then(res => {
                    dirtyListenerSemaphore.current = true;
                    mergeDealing(res, formikProps, id).then(()=> {
                        dirtyListenerSemaphore.current = false;
                        setLoaded(true);
                    });
                    if (autoSaveFormDraftActive) {
                        DealingsService.checkForDraft(demandId, partId).then(res => setDraftConfirm(res.result));
                    }
                });
        }
        // eslint-disable-next-line
    }, [])

    if (!isDealingElement || !loaded || working) {
        return (
            <center className={classes.loading}>
                <Loading/>
            </center>
        )
    }
    const stageId = getObjectValue(createId('stages.0.identity'), formikProps.values);

    const loadDealingDraft = () => {
        setDraftConfirm(false);
        setWorking(true);
        DealingsService.loadDraft(demandId, partId).then(res => {
            dirtyListenerSemaphore.current = true;
            mergeDealing(res.result, formikProps, id).then(()=> {
                dirtyListenerSemaphore.current = false;
            });
        });
        setWorking(false);
    }

    return (
        <>
            <GridBlock
                className={classes.root}
                column
            >
                <TextInput
                    formikProps={formikProps}
                    label={Tr('description')}
                    id={createId('description')}
                    multiline
                    required
                />
                {'Framework' === dealingsList.demandVariant &&
                <TextInput
                    id={createId('plannedContractorNumberForAgreement')}
                    label={Tr('planned_contractor_number_for_agreement')}
                    formikProps={formikProps}
                    required
                    limit={limits.PLANNED_CONTRACTOR_NUMBER}
                    type={INPUT_TYPE.INTEGER_POSITIVE_AMOUNT}
                    showErrorImmediately
                />
                }
                { !!dealingsList.planRequired &&
                    <Select
                        id={createId('planItemIdentity')}
                        label={Tr('dealing_plan_item')}
                        required
                        formikProps={formikProps}
                        items={dealingsList.planItems.map(x => createSelectItem(x.identity, x.name, x.number))}
                        />
                }

                <Box component="div" className={classes.cpvBox}>
                    <CPVInput
                        formikProps={formikProps}
                        id={createId('categoryItemCpv')}
                        label={Tr(isNonpublicProcedure() ? 'cpv_code_nonpublic' : 'cpv_code')}
                        required
                    />
                </Box>
                <RadioGroup
                    id={createId('termOption')}
                    label={Tr('term_deadline')}
                    formikProps={formikProps}
                    items={[
                        { label: Tr('date'), value: "date" },
                        { label: Tr('description'), value: "text" },
                    ]}
                />
                {getObjectValue(createId('termOption'), formikProps.values) === 'date' ?
                    <DatePicker
                        id={createId('termDate')}
                        label={Tr('choose_date')}
                        formikProps={formikProps}
                        minDate={new Date()}
                    />
                    :
                    <TextInput
                        id={createId('termDescription')}
                        label={Tr('description')}
                        formikProps={formikProps}
                        limit={InputLimits.procedure.basicInformation.TERM_DESCRIPTION}
                        multiline
                    />
                }
                <GridBlock
                    className={classes.grossNetGrid}
                    wrap='nowrap'
                >
                    <NetGrossField
                        required
                        formikProps={formikProps}
                        title={Tr('estimated_value')}
                        id={createId('amount')}
                    />
                    <TitledComponent
                        title={Tr('realization_value')}
                    >
                        <GridBlock>
                            <TextInput
                                id={createId('realizationBeforeOfferAmount.grossValue')}
                                formikProps={formikProps}
                                label={Tr('gross')}
                                type={INPUT_TYPE.NUMBER_19}
                                step="0.01"
                            />
                            <div/>
                        </GridBlock>
                    </TitledComponent>
                </GridBlock>

                <PositionsSection
                    formikProps={formikProps}
                    id={createId('items')}
                    dealingId={id}
                    priceType={dealingsList.priceType}
                />
                <PositionsParametersSection
                    formikProps={formikProps}
                    partId={partId}
                    stageId={stageId}
                    id={createId('stages.0.itemParameters')}
                />
                <ConditionsSection
                    formikProps={formikProps}
                    rootId={id}
                    partId={partId}
                    id={createId('stages.0.conditions')}
                    checkboxId={createId('stages.0.conditionsInSiwz')}
                    stageId={stageId}
                    dirtyListenerSemaphore={dirtyListenerSemaphore}
                />
                <CriteriaSection
                    formikProps={formikProps}
                    id={createId('stages.0.criterions')}
                    criteriaSumId={createId('stages.0._criteriaSum')}
                    stageId={stageId}
                    partId={partId}
                />
                <AdditionalPositionsSection
                    formikProps={formikProps}
                    partId={partId}
                    stageId={stageId}
                    id={createId('stages.0.parameters')}
                />
            </GridBlock>
            <Confirm
                open={draftConfirm}
                onConfirm={() => loadDealingDraft()}
                onCancel={() => setDraftConfirm(false)}
                confirmText="form_draft_load_confirm_text"
                confirmTitle="form_draft_load_confirm_title"
            />
        </>
    )
}

PartForm.propTypes = {
    classes: PropTypes.any,
}

export default withStyles(style)(withRouter(PartForm));